"use client";

import Link from "next/link";

import { RotatingSubscriptionIcon } from "./RotatingSubscriptionIcon";

/*
 * THE ICON CAROUSEL MOVED OUT to `RotatingSubscriptionIcon`. The subscriptions
 * page banner needed the same motif, and the alternative was a second copy of the
 * icon list, the interval and the keyframes. What stays here is the PILL — its
 * coral fill and its rotating conic-gradient border — because that treatment
 * belongs to a nav pill and not to the motif.
 */
export function SubscriptionsNavItem({ prefix }: { prefix: string }) {
  const href = `/shop/subscriptions/all-plans?prefix=${prefix}`;

  const iconStack = <RotatingSubscriptionIcon size={16} strokeWidth={1.9} />;

  return (
    <>
      {/* Desktop — labelled pill, mirrors the address chip's recipe */}
      <Link
        href={href}
        aria-label="Subscriptions"
        className="gl-sub-nav gl-sub-nav-pill hidden items-center gap-1.5 rounded-full px-3 py-1.5 text-[13px] font-medium transition lg:flex focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[color:var(--coral)] focus-visible:ring-offset-[color:var(--surface)]"
      >
        {iconStack}
        <span>Subscriptions</span>
      </Link>

      {/* Mobile / tablet — icon only, matches the search & cart buttons */}
      <Link
        href={href}
        aria-label="Subscriptions"
        title="Subscriptions"
        className="gl-sub-nav gl-sub-nav-mini flex h-10 w-10 items-center justify-center rounded-full transition lg:hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[color:var(--coral)] focus-visible:ring-offset-[color:var(--surface)]"
      >
        {iconStack}
      </Link>

      <style>{`
        .gl-sub-nav {
          position: relative;
          background: var(--coral);
          color: #fff;
          white-space: nowrap;
          box-shadow: 0 8px 20px -10px rgba(242, 98, 80, 0.85);
          transition: transform 180ms ease, box-shadow 180ms ease;
        }

        .gl-sub-nav:hover {
          transform: scale(1.04);
          box-shadow: 0 10px 24px -10px rgba(242, 98, 80, 0.95);
        }

        @property --gl-sub-angle {
          syntax: '<angle>';
          inherits: false;
          initial-value: 0deg;
        }

        .gl-sub-nav::before {
          content: "";
          position: absolute;
          inset: 0;
          border-radius: 999px;
          padding: 1.5px;
          /* White + gold, not coral — the sweep has to read against a coral
             fill now that the pill is filled. */
          background: conic-gradient(
            from var(--gl-sub-angle),
            transparent 0turn,
            #fff 0.12turn,
            var(--gold) 0.24turn,
            transparent 0.4turn,
            transparent 1turn
          );
          -webkit-mask:
            linear-gradient(#000 0 0) content-box,
            linear-gradient(#000 0 0);
          -webkit-mask-composite: xor;
          mask:
            linear-gradient(#000 0 0) content-box,
            linear-gradient(#000 0 0);
          mask-composite: exclude;
          animation: gl-sub-rotate 3.2s linear infinite;
          pointer-events: none;
        }

        @keyframes gl-sub-rotate {
          to { --gl-sub-angle: 360deg; }
        }

        @supports not (background: conic-gradient(from var(--gl-sub-angle), red, blue)) {
          .gl-sub-nav::before {
            background: linear-gradient(120deg, #fff, var(--gold));
            animation: none;
            opacity: 0.6;
          }
        }

        /* The icon carousel's own CSS is in globals.css — two components
           render it now. See RotatingSubscriptionIcon. */
      `}</style>
    </>
  );
}

export default SubscriptionsNavItem;
